import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";
import type { DealItem, SearchResult } from "@/types/api";

export default function SearchPage() {
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(query);
  const [results, setResults] = useState<DealItem[]>([]);
  const [loading, setLoading] = useState(!!query);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const startRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (reset: boolean, q?: string) => {
    const term = q ?? query;
    if (!term.trim()) return;
    const start = reset ? 0 : startRef.current;
    if (reset) { setLoading(true); setResults([]); } else setLoadingMore(true);
    try {
      const data = await api.search(term, start, 25, "ko") as SearchResult;
      const items = data.results || [];
      if (reset) { setResults(items); startRef.current = items.length; }
      else { setResults((p) => [...p, ...items]); startRef.current += items.length; }
      setHasMore(items.length >= 25);
    } catch { setHasMore(false); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [query]);

  useEffect(() => { search(true); }, [search]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting && hasMore && !loadingMore && !loading) search(false); }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loading, search]);

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`, { replace: true });
    setQuery(trimmed);
    startRef.current = 0;
    setHasMore(true);
    search(true, trimmed);
  };

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-4">
      {/* 검색 입력 */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="게임 검색..."
            autoFocus={!query}
            className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          />
        </div>
        <button onClick={handleSearch} className="bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-semibold active:scale-[0.98]">검색</button>
      </div>

      {query && <h1 className="text-sm font-bold text-gray-900 mb-1">"{query}" 검색 결과</h1>}
      {!loading && <p className="text-[10px] text-gray-400 dark:text-sub-text mb-4">{results.length}개의 결과</p>}

      {loading ? (
        <div className="space-y-1.5">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-[52px] rounded-xl" />)}</div>
      ) : results.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400 dark:text-sub-text">검색 결과가 없어요.</div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {results.map((item, idx) => (
            <div key={item.appid}>
            {idx > 0 && idx % 10 === 0 && <AdBanner className="my-2" />}
            <Link to={`/game/${item.appid}`} className="flex items-center gap-2 px-3 py-2 bg-card-bg rounded-xl border border-gray-200 dark:border-card-border hover:border-gray-300 hover:shadow-md transition-all group">
              <div className="shrink-0 w-[80px] h-[37px] sm:w-[120px] sm:h-[56px] rounded-lg overflow-hidden bg-hover-bg dark:bg-background">
                <img src={item.capsule_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${item.appid}/header.jpg`} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[11px] sm:text-sm text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors line-clamp-1">{item.name}</h3>
                {item.review_summary && <p className="text-[9px] text-gray-400 dark:text-muted mt-0.5">{item.review_summary}</p>}
              </div>
              <div className="shrink-0 text-right">
                {Number(item.discount_percent) > 0 && <span className="text-[9px] font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20 px-1 rounded">-{item.discount_percent}%</span>}
                <p className={`text-[11px] font-bold ${Number(item.discount_percent) > 0 ? "text-green-600 dark:text-green-400" : "text-gray-700 dark:text-gray-200"}`}>{item.final_price_text}</p>
              </div>
            </Link>
            </div>
          ))}
        </div>
      )}

      <div ref={sentinelRef} />
      {loadingMore && <Spinner />}
      <AdBanner className="my-4" />
    </div>
  );
}
